import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { Badge } from '../Badge/Badge';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface EmpresaOpcion {
  id: string;
  name: string;
  /** Abreviatura de 2 letras mostrada en el chip, ej: "TS" */
  abbr: string;
  establecimientos: EstablecimientoOpcion[];
  /** Indica si es la empresa demo (Senciyo SAC, RUC 20000000000) */
  isDemo?: boolean;
}

export interface EstablecimientoOpcion {
  id: string;
  name: string;
}

export interface FilaAsignacion {
  id: string;
  establecimientoId: string;
  rolId: string;
  rolNombre?: string;
}

export interface BloqueAsignacion {
  id: string;
  empresaId: string;
  filas: FilaAsignacion[];
}

export interface RolOpcion {
  id: string;
  name: string;
}

export interface AsignacionRolesProps {
  /** Empresas disponibles con sus establecimientos */
  empresas: EmpresaOpcion[];
  /** Roles disponibles para asignar (aparecen en el selector) */
  roles: RolOpcion[];
  /** Lista completa de roles para resolver nombres en badges (incluye roles no asignables) */
  rolesDisplay?: RolOpcion[];
  /** Valor controlado */
  value?: BloqueAsignacion[];
  /** Se llama cada vez que cambia la lista de asignaciones */
  onChange?: (asignaciones: BloqueAsignacion[]) => void;
  /** Desactiva toda interacción (modo solo lectura) */
  readOnly?: boolean;
  /** Label del botón "Agregar empresa" */
  addEmpresaLabel?: string;
  /** Label del botón "Agregar establecimiento" */
  addEstablecimientoLabel?: string;
  /** Muestra un warning si hay asignaciones incompletas */
  showIncompleteWarning?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

let _idCounter = 0;
const uid = () => `ra-${++_idCounter}`;

const esFilaCompleta = (fila: FilaAsignacion) =>
  !!fila.establecimientoId && !!fila.rolId;

const esBloqueCompleto = (bloque: BloqueAsignacion) =>
  !!bloque.empresaId &&
  bloque.filas.length > 0 &&
  bloque.filas.every(esFilaCompleta);

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: FilaAsignacionItem
// ─────────────────────────────────────────────────────────────────────────────

interface FilaAsignacionItemProps {
  fila: FilaAsignacion;
  todasLasFilas: FilaAsignacion[];
  establecimientos: EstablecimientoOpcion[];
  roles: RolOpcion[];
  empresaSeleccionada: boolean;
  readOnly: boolean;
  onChangeEst: (filaId: string, value: string) => void;
  onChangeRol: (filaId: string, value: string) => void;
  onRemove: (filaId: string) => void;
}

const FilaAsignacionItem: React.FC<FilaAsignacionItemProps> = ({
  fila,
  todasLasFilas,
  establecimientos,
  roles,
  empresaSeleccionada,
  readOnly,
  onChangeEst,
  onChangeRol,
  onRemove,
}) => {
  const completa = esFilaCompleta(fila);

  // Encontrar establecimientos ya asignados (excluyendo la fila actual)
  const establecimientosYaAsignados = new Set(
    todasLasFilas
      .filter(f => f.id !== fila.id && f.establecimientoId && f.rolId)
      .map(f => f.establecimientoId)
  );

  // Encontrar roles ya asignados al mismo establecimiento (excluyendo la fila actual)
  const rolesAsignadosAEsteEstablecimiento = new Set(
    todasLasFilas
      .filter(f => f.id !== fila.id && f.establecimientoId === fila.establecimientoId && f.rolId)
      .map(f => f.rolId)
  );

  const selectClasses = (disabled: boolean) =>
    [
      'w-full appearance-none rounded-lg px-3 h-8 pr-7',
      'text-ui-sm font-sans leading-tight outline-none',
      'border transition-colors duration-150',
      'bg-surface-1 text-primary',
      completa
        ? 'border-success focus:border-success focus:ring-1 focus:ring-success'
        : 'border-default focus:border-brand focus:ring-1 focus:ring-brand',
      disabled && 'opacity-50 cursor-not-allowed',
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <div className="grid grid-cols-[1fr_1fr_32px] gap-2 items-center">
      {/* Select Establecimiento */}
      <div className="relative">
        <select
          id={`asignacion-rol-establecimiento-${fila.id}`}
          name={`asignacionRolEstablecimiento${fila.id}`}
          value={fila.establecimientoId}
          disabled={!empresaSeleccionada || readOnly}
          onChange={(e) => onChangeEst(fila.id, e.target.value)}
          aria-label="Establecimiento"
          className={selectClasses(!empresaSeleccionada || readOnly)}
        >
          <option value="">Selecciona un establecimiento</option>
          {establecimientos.map((est) => {
            const yaAsignado = establecimientosYaAsignados.has(est.id);
            return (
              <option key={est.id} value={est.id} disabled={yaAsignado}>
                {est.name}{yaAsignado ? ' (ya tiene un rol asignado)' : ''}
              </option>
            );
          })}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-tertiary" />
      </div>

      {/* Select Rol */}
      <div className="relative">
        <select
          id={`asignacion-rol-rol-${fila.id}`}
          name={`asignacionRolRol${fila.id}`}
          value={fila.rolId}
          disabled={readOnly}
          onChange={(e) => onChangeRol(fila.id, e.target.value)}
          aria-label="Rol"
          className={selectClasses(readOnly)}
        >
          <option value="">Asigna un rol</option>
          {roles.map((rol) => {
            const yaAsignado = rolesAsignadosAEsteEstablecimiento.has(rol.id);
            return (
              <option key={rol.id} value={rol.id} disabled={yaAsignado}>
                {rol.name}{yaAsignado ? ' (ya asignado)' : ''}
              </option>
            );
          })}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-tertiary" />
      </div>

      {/* Botón quitar fila */}
      {!readOnly ? (
        <button
          id={`asignacion-rol-fila-quitar-${fila.id}`}
          name={`asignacionRolFilaQuitar${fila.id}`}
          type="button"
          aria-label="Quitar fila"
          onClick={() => onRemove(fila.id)}
          className={[
            'flex items-center justify-center w-8 h-8 rounded-md',
            'border border-default bg-surface-1 text-tertiary',
            'transition-colors duration-150',
            'hover:border-error hover:text-error hover:bg-error-bg',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
          ].join(' ')}
        >
          <XSmallIcon />
        </button>
      ) : (
        <div className="w-8" />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: BloqueAsignacion
// ─────────────────────────────────────────────────────────────────────────────

interface BloqueAsignacionProps {
  bloque: BloqueAsignacion;
  bloqueIndex: number;
  empresas: EmpresaOpcion[];
  roles: RolOpcion[];
  rolesDisplay: RolOpcion[];
  isOpen: boolean;
  readOnly: boolean;
  addEstablecimientoLabel: string;
  /** IDs de empresas ya usadas en otros bloques */
  empresasYaUsadas: Set<string>;
  onToggle: (id: string) => void;
  onRemoveBloque: (id: string) => void;
  onChangeEmpresa: (bloqueId: string, empresaId: string) => void;
  onAddFila: (bloqueId: string) => void;
  onRemoveFila: (bloqueId: string, filaId: string) => void;
  onChangeEst: (bloqueId: string, filaId: string, value: string) => void;
  onChangeRol: (bloqueId: string, filaId: string, value: string) => void;
}

const BloqueAsignacionItem: React.FC<BloqueAsignacionProps> = ({
  bloque,
  bloqueIndex,
  empresas,
  roles,
  rolesDisplay,
  isOpen,
  readOnly,
  addEstablecimientoLabel,
  empresasYaUsadas,
  onToggle,
  onRemoveBloque,
  onChangeEmpresa,
  onAddFila,
  onRemoveFila,
  onChangeEst,
  onChangeRol,
}) => {
  const empresa = empresas.find((c) => c.id === bloque.empresaId);
  const filasCompletas = bloque.filas.filter(esFilaCompleta);
  const completo = esBloqueCompleto(bloque);
  const tieneIncompleto = bloque.filas.length > 0 && !completo && !!bloque.empresaId;

  // Badges para el estado colapsado
  const badgePills = filasCompletas.slice(0, 2).map((fila) => {
    const rol = rolesDisplay.find((r) => r.id === fila.rolId);
    return rol?.name ?? fila.rolNombre ?? fila.rolId;
  });
  const extraCount = filasCompletas.length > 2 ? filasCompletas.length - 2 : 0;

  return (
    <div
      className={[
        'rounded-xl overflow-hidden transition-colors duration-150',
        'border',
        isOpen ? 'border-brand' : 'border-default',
      ].join(' ')}
    >
      {/* ── Header ── */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-label={`Asignación ${bloqueIndex + 1}: ${empresa?.name ?? 'Sin empresa'}`}
        onClick={() => onToggle(bloque.id)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle(bloque.id)}
        className={[
          'flex items-center gap-2.5 px-3.5 py-2.5 cursor-pointer select-none',
          'transition-colors duration-150',
          isOpen ? 'bg-brand-light' : 'bg-surface-2',
        ].join(' ')}
      >
        {/* Chip abreviatura */}
        <div
          className={[
            'shrink-0 flex items-center justify-center',
            'w-[30px] h-[30px] rounded-[7px]',
            'text-caption font-bold leading-none',
            'border transition-colors duration-150',
            isOpen
              ? 'bg-brand border-brand text-white'
              : completo
              ? 'bg-success border-success text-white'
              : 'bg-surface-1 border-default text-tertiary',
          ].join(' ')}
        >
          {empresa?.abbr ?? '?'}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-ui-sm font-semibold text-primary truncate">
              {empresa?.name ?? (
                <span className="text-tertiary font-normal italic">Sin empresa</span>
              )}
            </span>
            {empresa?.isDemo && (
              <Badge variant="amber" shape="rounded" noDot size="sm">Demo</Badge>
            )}
          </div>
          <div className="text-caption text-tertiary mt-0.5">
            {bloque.filas.length} fila{bloque.filas.length !== 1 ? 's' : ''} ·{' '}
            {filasCompletas.length} completa{filasCompletas.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Badges (solo colapsado) */}
        {!isOpen && (
          <div className="flex items-center gap-1 shrink-0 flex-wrap">
            {badgePills.map((label) => (
              <span
                key={label}
                className="text-caption font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded
                           bg-success-bg text-success border border-success"
              >
                {label}
              </span>
            ))}
            {extraCount > 0 && (
              <span className="text-caption font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded
                               bg-surface-2 text-tertiary border border-default">
                +{extraCount}
              </span>
            )}
            {tieneIncompleto && badgePills.length === 0 && (
              <span className="text-caption font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded
                               bg-warning-bg text-warning border border-warning">
                incompleto
              </span>
            )}
          </div>
        )}

        {/* Chevron */}
        <ChevronDownIcon
          className={[
            'shrink-0 text-tertiary transition-transform duration-200',
            isOpen ? 'rotate-180' : '',
          ].join(' ')}
        />

        {/* Quitar bloque */}
        {!readOnly && (
          <button
            id={`asignacion-rol-bloque-quitar-${bloque.id}`}
            name={`asignacionRolBloqueQuitar${bloque.id}`}
            type="button"
            aria-label={`Quitar empresa ${empresa?.name ?? ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveBloque(bloque.id);
            }}
            className={[
              'shrink-0 flex items-center justify-center w-7 h-7 rounded-md',
              'border border-default bg-surface-1 text-tertiary',
              'transition-colors duration-150',
              'hover:border-error hover:text-error hover:bg-error-bg',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
            ].join(' ')}
          >
            <XSmallIcon />
          </button>
        )}
      </div>

      {/* ── Body (expandido) ── */}
      <div
        className={[
          'overflow-hidden transition-all duration-300',
          isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0',
        ].join(' ')}
        aria-hidden={!isOpen}
      >
        <div className="px-3.5 pt-3.5 pb-4 border-t border-default">
          {/* Selector de empresa */}
          <div className="mb-3">
            <label className="block text-caption font-semibold uppercase tracking-wide text-secondary mb-1.5">
              Empresa
            </label>
            <div className="relative">
              <select
                id={`asignacion-rol-empresa-${bloque.id}`}
                name={`asignacionRolEmpresa${bloque.id}`}
                value={bloque.empresaId}
                disabled={readOnly}
                onChange={(e) => onChangeEmpresa(bloque.id, e.target.value)}
                className={[
                  'w-full appearance-none rounded-lg px-3 h-8 pr-7',
                  'text-ui-sm font-sans font-medium leading-tight outline-none',
                  'border border-default bg-surface-1 text-primary',
                  'focus:border-brand focus:ring-1 focus:ring-brand',
                  'transition-colors duration-150',
                  readOnly && 'opacity-50 cursor-not-allowed',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <option value="">Selecciona empresa</option>
                {empresas.map((c) => {
                  const yaUsada = empresasYaUsadas.has(c.id) && c.id !== bloque.empresaId;
                  return (
                    <option key={c.id} value={c.id} disabled={yaUsada}>
                      {c.name.length > 60 ? c.name.substring(0, 60) + '…' : c.name}{yaUsada ? ' (ya asignada)' : ''}
                    </option>
                  );
                })}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-tertiary" />
            </div>
          </div>

          {/* Tabla de filas */}
          {bloque.filas.length > 0 && (
            <>
              <div className="grid grid-cols-[1fr_1fr_32px] gap-2 pb-1.5 mb-1.5 border-b border-muted">
                <span className="text-caption font-bold uppercase tracking-wider text-tertiary">
                  Establecimiento
                </span>
                <span className="text-caption font-bold uppercase tracking-wider text-tertiary">
                  Rol
                </span>
                <span />
              </div>

              <div className="flex flex-col gap-1.5">
                {bloque.filas.map((fila) => (
                  <FilaAsignacionItem
                    key={fila.id}
                    fila={fila}
                    todasLasFilas={bloque.filas}
                    establecimientos={empresa?.establecimientos ?? []}
                    roles={roles}
                    empresaSeleccionada={!!bloque.empresaId}
                    readOnly={readOnly}
                    onChangeEst={(filaId, val) => onChangeEst(bloque.id, filaId, val)}
                    onChangeRol={(filaId, val) => onChangeRol(bloque.id, filaId, val)}
                    onRemove={(filaId) => onRemoveFila(bloque.id, filaId)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Agregar establecimiento */}
          {!readOnly && (
            <button
              id={`asignacion-rol-establecimiento-agregar-${bloque.id}`}
              name={`asignacionRolEstablecimientoAgregar${bloque.id}`}
              type="button"
              onClick={() => onAddFila(bloque.id)}
              className={[
                'inline-flex items-center gap-1.5 mt-2.5',
                'px-3 py-1.5 rounded-lg',
                'border border-dashed border-default',
                'text-ui-sm font-semibold text-tertiary',
                'transition-all duration-150',
                'hover:border-brand hover:border-solid hover:text-brand hover:bg-brand-light',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              ].join(' ')}
            >
              <PlusIcon />
              {addEstablecimientoLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal: AsignacionRoles
// ─────────────────────────────────────────────────────────────────────────────

export const AsignacionRoles: React.FC<AsignacionRolesProps> = ({
  empresas,
  roles,
  rolesDisplay,
  value,
  onChange,
  readOnly = false,
  addEmpresaLabel = 'Agregar empresa',
  addEstablecimientoLabel = 'Agregar establecimiento',
  showIncompleteWarning = false,
}) => {
  const rolesParaMostrar = rolesDisplay ?? roles;
  const [internalAsignaciones, setInternalAsignaciones] = useState<BloqueAsignacion[]>([]);
  const [openBloqueId, setOpenBloqueId] = useState<string | null>(() => (value ?? [])[0]?.id ?? null);
  const containerRef = useRef<HTMLDivElement>(null);

  const asignaciones = value ?? internalAsignaciones;

  const primerBloqueId = asignaciones[0]?.id;

  useLayoutEffect(() => {
    if (primerBloqueId) {
      setOpenBloqueId(primerBloqueId);
    } else {
      setOpenBloqueId(null);
    }
  }, [primerBloqueId]);

  const setAsignaciones = useCallback(
    (next: BloqueAsignacion[] | ((prev: BloqueAsignacion[]) => BloqueAsignacion[])) => {
      const resolved = typeof next === 'function' ? next(asignaciones) : next;
      if (!value) setInternalAsignaciones(resolved);
      onChange?.(resolved);
    },
    [asignaciones, value, onChange]
  );

  // Nuevo estado para rastrear si el usuario ha interactuado fuera del componente
  const [mostrarWarningIncompletos, setMostrarWarningIncompletos] = useState(false);

  const hayIncompletos =
    asignaciones.some((b) => b.empresaId && !esBloqueCompleto(b));

  // Ocultar warning cuando se completen las asignaciones
  useEffect(() => {
    if (!hayIncompletos) {
      setMostrarWarningIncompletos(false);
    }
  }, [hayIncompletos]);

  // ── Effect: Colapsar al hacer clic fuera y mostrar warning ──
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpenBloqueId(null);
        // Mostrar warning solo si hay incompletos y el usuario hizo clic fuera
        if (showIncompleteWarning && hayIncompletos) {
          setMostrarWarningIncompletos(true);
        }
      }
    };

    if (openBloqueId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openBloqueId, showIncompleteWarning, hayIncompletos]);

  // ── Handlers ──

  const handleToggle = (id: string) =>
    setOpenBloqueId((prev) => (prev === id ? null : id));

  const handleAddBloque = () => {
    const newBloque: BloqueAsignacion = {
      id: uid(),
      empresaId: '',
      filas: [{ id: uid(), establecimientoId: '', rolId: '' }],
    };
    setAsignaciones((prev) => [...prev, newBloque]);
    setOpenBloqueId(newBloque.id);
  };

  const handleRemoveBloque = (bloqueId: string) => {
    setAsignaciones((prev) => prev.filter((b) => b.id !== bloqueId));
    if (openBloqueId === bloqueId) setOpenBloqueId(null);
  };

  const handleChangeEmpresa = (bloqueId: string, empresaId: string) => {
    const empresa = empresas.find(e => e.id === empresaId);
    const establecimientoUnico = empresa?.establecimientos.length === 1
      ? empresa.establecimientos[0].id
      : '';

    setAsignaciones((prev) =>
      prev.map((b) =>
        b.id === bloqueId
          ? { ...b, empresaId, filas: b.filas.map((f) => ({ ...f, establecimientoId: establecimientoUnico, rolId: '' })) }
          : b
      )
    );
  };

  const handleAddFila = (bloqueId: string) => {
    setAsignaciones((prev) =>
      prev.map((b) =>
        b.id === bloqueId
          ? { ...b, filas: [...b.filas, { id: uid(), establecimientoId: '', rolId: '' }] }
          : b
      )
    );
  };

  const handleRemoveFila = (bloqueId: string, filaId: string) => {
    setAsignaciones((prev) =>
      prev.map((b) =>
        b.id === bloqueId ? { ...b, filas: b.filas.filter((f) => f.id !== filaId) } : b
      )
    );
  };

  const handleChangeEst = (bloqueId: string, filaId: string, val: string) => {
    setAsignaciones((prev) =>
      prev.map((b) =>
        b.id === bloqueId
          ? { ...b, filas: b.filas.map((f) => (f.id === filaId ? { ...f, establecimientoId: val, rolId: '' } : f)) }
          : b
      )
    );
  };

  const handleChangeRol = (bloqueId: string, filaId: string, val: string) => {
    setAsignaciones((prev) =>
      prev.map((b) =>
        b.id === bloqueId
          ? { ...b, filas: b.filas.map((f) => (f.id === filaId ? { ...f, rolId: val } : f)) }
          : b
      )
    );
  };

  // ── Vista de solo lectura ──
  if (readOnly) {
    if (asignaciones.length === 0) {
      return (
        <div className="text-center py-4 text-ui-sm text-tertiary border border-dashed border-default rounded-xl">
          Sin accesos asignados
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2.5">
        {asignaciones.map((bloque) => {
          const empresa = empresas.find((e) => e.id === bloque.empresaId);
          const filasCompletas = bloque.filas.filter(esFilaCompleta);
          return (
            <div key={bloque.id} className="rounded-xl border border-default overflow-hidden">
              <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-surface-2">
                <div className="shrink-0 flex items-center justify-center w-[30px] h-[30px] rounded-[7px] border border-default bg-surface-1 text-caption font-bold text-tertiary">
                  {empresa?.abbr ?? '?'}
                </div>
                <span className="text-ui-sm font-semibold text-primary truncate">
                  {empresa?.name ?? <span className="text-tertiary font-normal italic">Sin empresa</span>}
                </span>
              </div>
              {filasCompletas.length > 0 && (
                <div className="px-3.5 pt-3 pb-3.5 border-t border-default flex flex-col gap-1.5">
                  <div className="grid grid-cols-2 gap-2 pb-1.5 mb-0.5 border-b border-muted">
                    <span className="text-caption font-bold uppercase tracking-wider text-tertiary">Establecimiento</span>
                    <span className="text-caption font-bold uppercase tracking-wider text-tertiary">Rol</span>
                  </div>
                  {filasCompletas.map((fila) => {
                    const est = empresa?.establecimientos.find((e) => e.id === fila.establecimientoId);
                    const rol = rolesParaMostrar.find((r) => r.id === fila.rolId);
                    return (
                      <div key={fila.id} className="grid grid-cols-2 gap-2 items-center">
                        <span className="text-ui-sm text-primary truncate">{est?.name ?? fila.establecimientoId}</span>
                        <span className="text-ui-sm text-secondary">{rol?.name ?? fila.rolNombre ?? fila.rolId}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col gap-3">
      {/* Banner de incompletos - solo mostrar si el usuario interactuó fuera */}
      {mostrarWarningIncompletos && hayIncompletos && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg
                        bg-warning-bg border border-warning text-warning text-ui-sm leading-snug">
          <WarningIcon className="shrink-0 mt-0.5" />
          Tienes asignaciones incompletas. Completa todos los campos antes de guardar.
        </div>
      )}

      {/* Estado vacío */}
      {asignaciones.length === 0 && (
        <div className="text-center py-4 text-ui-sm text-tertiary
                        border border-dashed border-default rounded-xl">
          Sin empresas asignadas. Haz clic en "{addEmpresaLabel}" para comenzar.
        </div>
      )}

      {/* Bloques */}
      {asignaciones.length > 0 && (
        <div className="flex flex-col gap-2.5">
          {asignaciones.map((bloque, idx) => {
            const empresasYaUsadas = new Set(
              asignaciones
                .filter((b) => b.id !== bloque.id && !!b.empresaId)
                .map((b) => b.empresaId)
            );
            return (
              <BloqueAsignacionItem
                key={bloque.id}
                bloque={bloque}
                bloqueIndex={idx}
                empresas={empresas}
                roles={roles}
                rolesDisplay={rolesParaMostrar}
                isOpen={openBloqueId === bloque.id}
                readOnly={readOnly}
                addEstablecimientoLabel={addEstablecimientoLabel}
                empresasYaUsadas={empresasYaUsadas}
                onToggle={handleToggle}
                onRemoveBloque={handleRemoveBloque}
                onChangeEmpresa={handleChangeEmpresa}
                onAddFila={handleAddFila}
                onRemoveFila={handleRemoveFila}
                onChangeEst={handleChangeEst}
                onChangeRol={handleChangeRol}
              />
            );
          })}
        </div>
      )}

      {/* Botón agregar empresa */}
      {!readOnly && empresas.length > asignaciones.filter(b => b.empresaId).length && (() => {
        const hayAlgunIncompleto = asignaciones.some((b) => !esBloqueCompleto(b));
        const todasAsignadas = empresas.length > 0 && asignaciones.filter(b => b.empresaId).length >= empresas.length;
        const deshabilitado = hayAlgunIncompleto || todasAsignadas;
        const titulo = hayAlgunIncompleto
          ? 'Completa los accesos actuales antes de agregar otro'
          : todasAsignadas
          ? 'Ya se asignaron todas las empresas disponibles'
          : undefined;
        return (
          <button
            id="asignacion-rol-empresa-agregar"
            name="asignacionRolEmpresaAgregar"
            type="button"
            onClick={deshabilitado ? undefined : handleAddBloque}
            disabled={deshabilitado}
            title={titulo}
            className={[
              'flex items-center justify-center gap-1.5 w-full',
              'py-2 rounded-xl',
              'border border-dashed',
              'text-ui-sm font-semibold',
              'transition-all duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              deshabilitado
                ? 'border-default text-disabled opacity-70 cursor-not-allowed'
                : 'border-default text-tertiary hover:border-brand hover:border-solid hover:text-brand hover:bg-brand-light',
            ].join(' ')}
          >
            <PlusIcon />
            {addEmpresaLabel}
          </button>
        );
      })()}
    </div>
  );
};

AsignacionRoles.displayName = 'AsignacionRoles';

// ─────────────────────────────────────────────────────────────────────────────
// Iconos inline
// ─────────────────────────────────────────────────────────────────────────────

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    width="14" height="14" viewBox="0 0 12 12" fill="none"
    className={className}
    aria-hidden="true"
  >
    <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XSmallIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const WarningIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className={className} aria-hidden="true">
    <path d="M8 6v4M8 11.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M6.8 2.5L1.5 12a1.4 1.4 0 001.2 2h10.6a1.4 1.4 0 001.2-2L9.2 2.5a1.4 1.4 0 00-2.4 0z"
      stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);
